// Turns the /summary payload (city_explorer_data_routes.get_location_summary)
// into the landing page's "At a glance" tiles. Each tile links to the live map
// category that explains it, matched by the map's `component` so any location
// built from the same live maps gets headlines without per-state code.

export const hasLiveMaps = (location) =>
  (location?.categories || []).some((category) =>
    (category.files || []).some((file) => file.renderer === 'live')
  );

export const categoryForComponent = (location, component, focus = null) =>
  (location?.categories || []).find((category) =>
    (category.files || []).some(
      (file) =>
        file.renderer === 'live' &&
        file.component === component &&
        (!focus || file.props?.focus === focus)
    )
  ) || null;

const roundedPercent = (change) => Math.round(Number(change) * 1000) / 10;

// Tile value color: 'count' for "N of M" values, 'positive' / 'negative' /
// 'neutral' for percent changes, judged on the rounded value shown.
export const toneForChange = (change) => {
  if (change === null || change === undefined || !Number.isFinite(Number(change))) return 'neutral';
  const pct = roundedPercent(change);
  if (pct > 0) return 'positive';
  if (pct < 0) return 'negative';
  return 'neutral';
};

export const formatSignedPercent = (change) => {
  const pct = roundedPercent(change);
  if (pct === 0) return '0%';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

export const formatCoveragePercent = (share) => {
  if (share === null || share === undefined || !Number.isFinite(Number(share))) return null;
  return `${Math.round(Number(share) * 100)}%`;
};

const directionVerb = (change) => {
  if (change > 0) return 'grow';
  if (change < 0) return 'shrink';
  return 'hold steady';
};

const hasCount = (counts) => Boolean(counts && counts.total > 0);

const k12Highlight = (summary, location, name) => {
  const k12 = summary.k12;
  const category =
    categoryForComponent(location, 'SchoolDistrictsMap') ||
    categoryForComponent(location, 'WardMetricsMap');
  if (!k12 || k12.change === null || k12.change === undefined || !category) return null;
  const by = k12.futureYear ? ` by ${k12.futureYear}` : '';
  const districts = summary.districts;
  const regions = summary.regions;
  const areaNote = hasCount(districts)
    ? ` ${districts.declining} of ${districts.total} districts are projected to decline.`
    : hasCount(regions)
      ? ` ${regions.declining} of ${regions.total} wards are projected to decline.`
      : '';
  return {
    id: 'k12',
    label: 'Child population',
    value: formatSignedPercent(k12.change),
    tone: toneForChange(k12.change),
    text: `${name}'s K-12 population is projected to ${directionVerb(k12.change)}${by}.${areaNote}`,
    categoryId: category.id,
  };
};

const countiesHighlight = (summary, location) => {
  const counties = summary.counties;
  const category = categoryForComponent(location, 'CountiesMap');
  if (!hasCount(counties) || !category) return null;
  const by = summary.k12?.futureYear || summary.years?.esriFutureYear;
  const suffix = by ? ` by ${by}` : '';
  const declining = counties.declining > 0 || counties.growing === 0;
  return {
    id: 'counties',
    label: 'Counties',
    value: `${declining ? counties.declining : counties.growing} of ${counties.total}`,
    tone: 'count',
    text: declining
      ? `counties are projected to lose school-age children${suffix}.`
      : `counties are projected to gain school-age children${suffix}.`,
    categoryId: category.id,
  };
};

const enrollmentHighlight = (summary, location) => {
  const enrollment = summary.enrollment;
  const category = categoryForComponent(location, 'SchoolDistrictsMap');
  if (!enrollment || enrollment.change === null || enrollment.change === undefined || !category) {
    return null;
  }
  const span =
    enrollment.comparisonYear && enrollment.currentYear
      ? ` from ${enrollment.comparisonYear} to ${enrollment.currentYear}`
      : '';
  return {
    id: 'enrollment',
    label: 'Public enrollment',
    value: formatSignedPercent(enrollment.change),
    tone: toneForChange(enrollment.change),
    text: `Public school enrollment ${
      enrollment.change < 0 ? 'fell' : enrollment.change > 0 ? 'rose' : 'held steady'
    }${span}, to ${Number(enrollment.current).toLocaleString()} students.`,
    categoryId: category.id,
  };
};

const chartersHighlight = (summary, location) => {
  const charters = summary.charters;
  const category = categoryForComponent(location, 'CharterEnrollmentRiskMap');
  if (!hasCount(charters) || !category) return null;
  const since = charters.comparisonYear ? ` since ${charters.comparisonYear}` : '';
  return {
    id: 'charters',
    label: 'Charter schools',
    value: `${charters.declining} of ${charters.total}`,
    tone: 'count',
    text: `charter schools have seen enrollment decline${since}.`,
    categoryId: category.id,
  };
};

const desertBandLabel = (gradeBand) => {
  if (gradeBand === 'K5') return 'K–5';
  if (gradeBand === '68') return '6–8';
  if (gradeBand === '912') return '9–12';
  return gradeBand || 'K–5';
};

const desertHighlight = (summary, location, name, spec) => {
  const category = categoryForComponent(location, spec.component);
  const stats = summary.deserts?.[spec.kind];
  const value = formatCoveragePercent(stats?.coveragePct);
  if (!category || !value) return null;
  const band = desertBandLabel(summary.deserts?.gradeBand);
  return {
    id: `${spec.kind}Desert`,
    label: spec.label,
    value,
    tone: 'count',
    text: spec.text({ name, band }),
    categoryId: category.id,
  };
};

const DESERT_HIGHLIGHTS = [
  {
    kind: 'charter',
    component: 'CharterDesertMap',
    label: 'Charter coverage',
    text: ({ name, band }) =>
      `of ${name} is within a 15-minute walk of a ${band} charter school.`,
  },
  {
    kind: 'quality',
    component: 'DistrictQualityDesertMap',
    label: 'Quality coverage',
    text: ({ name, band }) =>
      `of ${name} is within a 15-minute walk of a ${band} quality district school.`,
  },
  {
    kind: 'combined',
    component: 'CombinedDesertMap',
    label: 'Combined coverage',
    text: ({ name, band }) =>
      `of ${name} is reached by a ${band} charter or quality district school within a 15-minute walk.`,
  },
];

export const formatCount = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? null
    : Math.round(Number(value)).toLocaleString();

export const travelPhrase = (stateConfig) =>
  `${stateConfig?.travelMinutes || 10}-minute ${stateConfig?.travelMode === 'walk' ? 'walk' : 'drive'}`;

// Statewide letter-grade locations (Arkansas): tiles from the directory-built
// `schools` section, linked to the dynamics / quality / charter maps.
const statewideHighlights = (summary, location, name) => {
  const schools = summary.schools;
  const config = summary.stateConfig || {};
  const dynamics = categoryForComponent(location, 'StateDynamicsMap');
  const quality = categoryForComponent(location, 'SchoolLocationsMap', 'quality');
  const charters = categoryForComponent(location, 'SchoolLocationsMap', 'charter');
  const tiles = [];
  const children = formatCount(summary.k12?.current);
  if (dynamics && children) {
    const districtCount = summary.districts?.total;
    const k12 = summary.k12;
    const projection =
      k12?.change !== null && k12?.change !== undefined
        ? ` The K-12 population is projected to ${directionVerb(k12.change)} ${formatCoveragePercent(Math.abs(k12.change))}${
            k12.futureYear ? ` by ${k12.futureYear}` : ''
          }.`
        : '';
    tiles.push({
      id: 'children',
      label: 'School-age children',
      value: children,
      tone: 'count',
      text: `children live in ${name}, served by ${schools.total.toLocaleString()} public schools${
        districtCount ? ` in ${districtCount} districts` : ''
      }.${projection}`,
      categoryId: dynamics.id,
    });
  }
  const declining = schools.declining;
  const decliningValue = formatCoveragePercent(declining?.pctDeclining10);
  if (dynamics && decliningValue) {
    const since = schools.comparisonYear ? ` since ${schools.comparisonYear}` : ' over five years';
    tiles.push({
      id: 'decliningSchools',
      label: 'Declining schools',
      value: decliningValue,
      tone: 'negative',
      text: `of public schools lost 10% or more of their enrollment${since} (${declining.declining10.toLocaleString()} of ${declining.eligible.toLocaleString()}).`,
      categoryId: dynamics.id,
    });
  }
  const tiers = schools.quality;
  const rated = tiers ? tiers.quality + tiers.notQuality : 0;
  if (quality && rated > 0) {
    const year = tiers.accountabilityYear ? ` (${tiers.accountabilityYear})` : '';
    tiles.push({
      id: 'qualitySchools',
      label: 'Quality schools',
      value: `${tiers.quality.toLocaleString()} of ${rated.toLocaleString()}`,
      tone: 'count',
      text: `rated schools earned ${config.qualityLabel ? `an ${config.qualityLabel}` : 'a quality rating'}${year}.`,
      categoryId: quality.id,
    });
  }
  const footprint = schools.charterFootprint;
  if (charters && schools.charter > 0) {
    const coverage = summary.deserts?.charter?.childrenEstimate?.childrenCoveredPct;
    const coverageText =
      coverage !== null && coverage !== undefined
        ? ` About ${formatCoveragePercent(coverage)} of ${desertBandLabel(summary.deserts?.gradeBand)} children live within a ${travelPhrase(config)} of one.`
        : '';
    const enrollment = formatCount(footprint?.enrollment);
    tiles.push({
      id: 'charterFootprint',
      label: 'Charter schools',
      value: schools.charter.toLocaleString(),
      tone: 'count',
      text: `charter schools${enrollment ? ` enroll ${enrollment} students` : ' operate statewide'}.${coverageText}`,
      categoryId: charters.id,
    });
  }
  return tiles;
};

export const buildHighlights = (summary, location) => {
  if (!summary || !location) return [];
  const name = String(location.name || '').trim() || 'This market';
  if (summary.schools) return statewideHighlights(summary, location, name);
  return [
    k12Highlight(summary, location, name),
    countiesHighlight(summary, location),
    enrollmentHighlight(summary, location),
    chartersHighlight(summary, location),
    ...DESERT_HIGHLIGHTS.map((spec) => desertHighlight(summary, location, name, spec)),
  ].filter(Boolean);
};
